import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Eye, EyeOff, Package, ShoppingCart, BarChart2, Users, ArrowLeftRight, Brain, Truck, FileText, Settings, Zap } from 'lucide-react';
import './Login.css';

const features = [
  { icon: Package, title: 'Inventory Management', desc: 'Real-time stock tracking, low-stock alerts, and multi-location warehouse control.' },
  { icon: ShoppingCart, title: 'Point of Sale (POS)', desc: 'Fast sales processing with barcode scanning, discounts, and multiple payment methods.' },
  { icon: Truck, title: 'Purchase & Receiving', desc: 'Manage supplier orders, goods receipts, and purchase returns seamlessly.' },
  { icon: FileText, title: 'Invoicing & EDI', desc: 'Auto-generate invoices and EDI flat files for seamless customer integrations.' },
  { icon: BarChart2, title: 'Reports & Analytics', desc: 'Profit & loss, stock reports, sales trends, and tax summaries at a glance.' },
  { icon: Users, title: 'Customer & Supplier CRM', desc: 'Manage contacts, credit limits, payment history, and loyalty programs.' },
  { icon: ArrowLeftRight, title: 'Stock Transfers', desc: 'Move products between warehouses and locations with full audit trail.' },
  { icon: Settings, title: 'Role-Based Access', desc: 'Granular permissions for staff, managers, and admins across all modules.' },
  { icon: Brain, title: 'AI Warehouse Intelligence', desc: 'Smart reorder suggestions, demand forecasting, and slow-moving stock alerts.', ai: true },
  { icon: Zap, title: 'QuickBooks Integration', desc: 'Sync sales, purchases, payments, and expenses directly with QuickBooks for seamless accounting.' },
];

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    if (!formData.password.trim()) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    setErrors({});
    try {
      const response = await login(formData.username, formData.password);
      if (response.success) {
        navigate('/');
      } else {
        setErrors({ general: response.message || 'Incorrect Username, Password, Or user not active.' });
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrors({ general: error.message || 'Incorrect Username, Password, Or user not active.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      {/* Left Panel - Login Form */}
      <div className="login-left-panel">
        <div className="login-left-content">
          <div className="login-logo">
            <div className="login-logo-icon">
              <Package size={28} color="white" />
            </div>
            <span className="login-logo-text">et-pos</span>
          </div>

          <div className="login-welcome">
            <h1>Welcome Back</h1>
            <p>Login to your et-pos</p>
          </div>

          {errors.general && (
            <div className="error-message">{errors.general}</div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={`form-input ${errors.username ? 'error' : ''}`}
                placeholder="Username"
                autoComplete="username"
              />
              {errors.username && <span className="error-text">{errors.username}</span>}
            </div>

            <div className="form-group">
              <div className="password-label-row">
                <label htmlFor="password">Password</label>
                <a href="/forgot-password" className="forgot-password">Forgot Your Password?</a>
              </div>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`form-input ${errors.password ? 'error' : ''}`}
                  placeholder="Password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <span className="error-text">{errors.password}</span>}
            </div>

            <div className="form-remember">
              <label className="remember-me">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                />
                Remember Me
              </label>
            </div>

            <button type="submit" className="login-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <div className="login-footer">
            <p>© 2026 et-pos. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Feature Showcase */}
      <div className="login-right-panel">
        <div className="login-right-content">
          <div className="login-hero-text">
            <h2>Your Complete Warehouse<br />&amp; POS Solution</h2>
            <p>Everything you need to run your warehouse business smarter and faster.</p>
          </div>
          <div className="login-features-grid">
            {features.map((feature, idx) => (
              <div className="login-feature-card" key={idx}>
                <div className="login-feature-icon">
                  <feature.icon size={18} />
                  {feature.ai && <span className="ai-badge">AI</span>}
                </div>
                <div className="login-feature-info">
                  <h4>{feature.title}{feature.ai && <span className="ai-badge-inline"> AI</span>}</h4>
                  <p>{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="login-tagline">
            <strong>Smarter Operations. Stronger Control.</strong> — Built for warehouse businesses like yours.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
